/**
 * PDF を「置くだけで保存」する欄 (TimeWaver 発注書類 / 2026-10)。
 *
 * - ドラッグ＆ドロップ、またはクリックしてファイル選択。選んだ時点で onFiles を呼ぶ (確認画面なし)
 * - PDF 以外・20MB 超は toast で弾く (storage.rules / uploadManufacturerQuote と同じ上限)
 */
import { Upload } from 'lucide-react';
import { useRef, useState } from 'react';
import { toast } from 'sonner';

const MAX_BYTES = 20 * 1024 * 1024;

interface Props {
  label: string;
  /** 補足 (欄の下に小さく表示) */
  hint?: string;
  multiple?: boolean;
  disabled?: boolean;
  onFiles: (files: File[]) => void;
}

export function PdfDropZone({ label, hint, multiple = false, disabled = false, onFiles }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const accept = (list: FileList | null) => {
    if (!list || list.length === 0) return;
    const picked = multiple ? Array.from(list) : [list[0]];
    const ok: File[] = [];
    for (const f of picked) {
      if (f.type !== 'application/pdf') {
        toast.error(`PDF ではないため保存しませんでした: ${f.name}`);
        continue;
      }
      if (f.size > MAX_BYTES) {
        toast.error(`20MB を超えるため保存しませんでした: ${f.name}`);
        continue;
      }
      ok.push(f);
    }
    if (ok.length > 0) onFiles(ok);
  };

  return (
    <div className="space-y-1">
      <button
        type="button"
        disabled={disabled}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          if (!disabled) accept(e.dataTransfer.files);
        }}
        className={`w-full rounded-md border-[1.5px] px-3 py-3 text-xs inline-flex items-center justify-center gap-2 text-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
          dragOver
            ? 'border-solid border-[var(--accent)] bg-[var(--accent-subtle)] text-[var(--text-primary)]'
            : 'border-dashed border-[var(--border)] bg-[var(--bg-elevated)] text-[var(--text-secondary)] hover:border-[var(--accent)] hover:text-[var(--text-primary)]'
        }`}
      >
        <Upload className="size-4 shrink-0 pointer-events-none" />
        <span className="pointer-events-none">{label}</span>
      </button>
      {hint && <p className="text-[10px] text-[var(--text-muted)]">{hint}</p>}
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf"
        multiple={multiple}
        className="hidden"
        onChange={(e) => {
          accept(e.target.files);
          e.target.value = '';
        }}
      />
    </div>
  );
}
